import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Offcanvas, Toast } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';
import { getTodayLocalDateString } from '../utils/dateUtils';
import { useUpdatePantryIngredient, useAddPantryIngredient, useConfirmHasEnough, useConfirmHasLessThanEnough } from '../hooks/pantryIngredient';
import ErrorBoundary from '../components/ErrorBoundary';
import GroceryDrawer from '../components/GroceryDrawer';
import RecipesForIngredientDrawer from '../components/RecipesForIngredientDrawer';
import '../index.css';

const PantryAndGroceriesDrawerWithAddAdditionalQuantity = ({ isOpen, onClose, mealPlanId }) => {
    const navigate = useNavigate();
    const { pgUser, sessionToken } = useAuth();
    const { updatePantryIngredient } = useUpdatePantryIngredient();
    const { addPantryIngredient } = useAddPantryIngredient();
    const { confirmHasEnough } = useConfirmHasEnough();
    const { confirmHasLessThanEnough } = useConfirmHasLessThanEnough();
    
    // State declarations
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [pantryId, setPantryId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentGroceryList, setCurrentGroceryList] = useState(null);
    const [pantryIngredients, setPantryIngredients] = useState([]);
    const [combinedData, setCombinedData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingQuantityId, setEditingQuantityId] = useState(null);
    const [editingQuantity, setEditingQuantity] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success');
    const [initialCategories, setInitialCategories] = useState(null);
    const [isGroceryDrawerOpen, setIsGroceryDrawerOpen] = useState(false);
    const [isRecipeDrawerOpen, setIsRecipeDrawerOpen] = useState(false);
    const [selectedIngredientForRecipes, setSelectedIngredientForRecipes] = useState(null);
    const [checkedIngredients, setCheckedIngredients] = useState(new Set());
    const [buttonStates, setButtonStates] = useState(new Map());
    const [groceryDrawerRefreshKey, setGroceryDrawerRefreshKey] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Initialize pgUser-dependent state
    useEffect(() => {
        if (!pgUser) {
            setIsAuthLoading(false);
            return;
        }
        setPantryId(pgUser?.pantry_id || null);
        setIsAuthLoading(false);
    }, [pgUser]);

    // Handle responsive behavior
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Helper function to format quantity display
    const formatQuantity = (quantity) => {
        const num = Number(quantity);
        if (Number.isInteger(num)) {
            return num.toString();
        }
        return num.toFixed(2).replace(/\.?0+$/, '');
    };

    // Helper function to compare floating point numbers with tolerance
    const isGreaterOrEqual = (a, b, epsilon = 0.01) => {
        return a >= b - epsilon;
    };

    // Helper function to show toast notifications
    const showToastNotification = (message, type = 'success') => {
        setToastMessage(message);
        setToastType(type);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    // Helper function to determine which week a grocery list belongs to
    const determineWeekFromDates = (startDate, endDate) => {
        if (!startDate || !endDate) return 'this';
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const start = new Date(startDate + 'T00:00:00');
        const end = new Date(endDate + 'T00:00:00');
        
        // If today falls within the date range, it's "this week"
        if (today >= start && today <= end) {
            return 'this';
        }
        
        // If the start date is in the future, it's "next week"
        if (start > today) {
            return 'next';
        }
        
        // Default to "this" if dates are in the past
        return 'this';
    };

    // Fetch current meal plan
    const fetchCurrentMealPlan = useCallback(async () => {
        if (!pgUser?.id || !sessionToken) return null;

        try {
            const response = await fetchWithCSRF(`/api/v1/meal-plan/current/${pgUser.id}`, {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch current meal plan');
            }

            const data = await response.json();
            if (data && data.length > 0) {
                return data[0];
            }
            return null;
        } catch (err) {
            console.error('Error fetching current meal plan:', err);
            return null;
        }
    }, [pgUser?.id, sessionToken]);

    // Fetch required ingredients for meal plan
    const fetchMealPlanRequiredIngredients = useCallback(async (mealPlanId) => {
        if (!mealPlanId || !pgUser?.id || !sessionToken) return [];

        try {
            console.log('🔍 [PantryAndGroceriesDrawer] Fetching required ingredients for meal plan ID:', mealPlanId);
            const response = await fetchWithCSRF(`/api/v1/meal-plan/${mealPlanId}/app_user/${pgUser.id}/required_ingredients`, {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (!response.ok) {
                console.error('❌ [PantryAndGroceriesDrawer] Failed to fetch meal plan required ingredients, status:', response.status);
                throw new Error('Failed to fetch meal plan required ingredients');
            }

            const data = await response.json();
            console.log('✅ [PantryAndGroceriesDrawer] Meal plan required ingredients received, count:', Array.isArray(data) ? data.length : 0);
            console.log('📋 [PantryAndGroceriesDrawer] Required ingredients list:', data);
            return Array.isArray(data) ? data : [];
        } catch (err) {
            console.error('❌ [PantryAndGroceriesDrawer] Error fetching meal plan required ingredients:', err);
            return [];
        }
    }, [pgUser?.id, sessionToken]);

    // Fetch grocery list by meal plan ID or current grocery list
    const fetchCurrentGroceryList = useCallback(async () => {
        if (!pgUser?.id || !sessionToken) return null;

        try {
            let response;
            
            // If mealPlanId is provided, fetch by meal plan ID
            if (mealPlanId) {
                console.log('🔍 [PantryAndGroceriesDrawer] Fetching grocery list by meal plan ID:', mealPlanId);
                response = await fetchWithCSRF(`/api/v1/grocery-list/meal_plan/${mealPlanId}/app_user/${pgUser.id}`, {
                    headers: { 'Authorization': `Bearer ${sessionToken}` }
                });
            } else {
                // Otherwise, fetch current grocery list by date
                const today = getTodayLocalDateString();
                console.log('🔍 [PantryAndGroceriesDrawer] Fetching current grocery list by date:', today);
                response = await fetchWithCSRF(`/api/v1/grocery-list/current/${pgUser.id}/date/${today}`, {
                    headers: { 'Authorization': `Bearer ${sessionToken}` }
                });
            }

            if (!response.ok) {
                console.error('❌ [PantryAndGroceriesDrawer] Failed to fetch grocery list, status:', response.status);
                throw new Error('Failed to fetch grocery list');
            }

            const data = await response.json();
            console.log('✅ [PantryAndGroceriesDrawer] Grocery list data received:', data);
            
            if (data && data.length > 0 && data[0].data) {
                console.log('📦 [PantryAndGroceriesDrawer] Grocery list items count:', data[0].data.grocery_list_ingredients?.length || 0);
                return data[0].data;
            }
            console.warn('⚠️ [PantryAndGroceriesDrawer] No grocery list data found');
            return null;
        } catch (err) {
            console.error('❌ [PantryAndGroceriesDrawer] Error fetching grocery list:', err);
            return null;
        }
    }, [pgUser?.id, sessionToken, mealPlanId]);

    // Fetch pantry ingredients
    const fetchPantryIngredients = useCallback(async () => {
        if (!pantryId || !pgUser?.id || !sessionToken) return [];

        try {
            console.log('🔍 [PantryAndGroceriesDrawer] Fetching pantry ingredients for pantry ID:', pantryId);
            const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pantryId}/app_user/${pgUser.id}`, {
                headers: { 'Authorization': `Bearer ${sessionToken}` }
            });

            if (!response.ok) {
                console.error('❌ [PantryAndGroceriesDrawer] Failed to fetch pantry ingredients, status:', response.status);
                throw new Error('Failed to fetch pantry ingredients');
            }

            const data = await response.json();
            console.log('✅ [PantryAndGroceriesDrawer] Pantry ingredients received, count:', Array.isArray(data) ? data.length : 0);
            return Array.isArray(data) ? data : [];
        } catch (err) {
            console.error('❌ [PantryAndGroceriesDrawer] Error fetching pantry ingredients:', err);
            return [];
        }
    }, [pantryId, pgUser?.id, sessionToken]);

    // Combine pantry and meal plan required ingredients data
    const combineData = useCallback((pantryItems, groceryListData, mealPlanRequiredIngredients) => {
        console.log('🔄 [PantryAndGroceriesDrawer] Combining data...');
        console.log('  - Pantry items:', pantryItems?.length || 0);
        console.log('  - Grocery list data:', groceryListData);
        console.log('  - Meal plan required ingredients:', mealPlanRequiredIngredients?.length || 0);
        
        const groceryIngredients = groceryListData?.grocery_list_ingredients || [];
        console.log('  - Grocery ingredients count:', groceryIngredients.length);
        
        const mealPlanRequiredMap = new Map();
        (mealPlanRequiredIngredients || []).forEach(item => {
            mealPlanRequiredMap.set(item.ingredient_id, {
                quantity: item.quantity,
                ingredient_name: item.ingredient_name
            });
        });
        console.log('  - Meal plan required map size:', mealPlanRequiredMap.size);
        
        const groceryMap = new Map();
        groceryIngredients.forEach(item => {
            groceryMap.set(item.ingredient_id, {
                quantity: item.quantity,
                unit_of_measurement: item.unit_of_measurement,
                ingredient_name: item.ingredient_name,
                primary_food_group: item.primary_food_group
            });
        });

        const pantryMap = new Map();
        pantryItems.forEach(item => {
            const existing = pantryMap.get(item.ingredient_id);
            if (existing) {
                pantryMap.set(item.ingredient_id, {
                    ...existing,
                    quantity: existing.quantity + parseFloat(item.quantity),
                    pantry_ingredient_ids: [...existing.pantry_ingredient_ids, item.id],
                    is_confirmed: existing.is_confirmed || item.is_confirmed,
                    estimated_cost_in_cents: (existing.estimated_cost_in_cents || 0) + (item.estimated_cost_in_cents || 0)
                });
            } else {
                pantryMap.set(item.ingredient_id, {
                    ingredient_id: item.ingredient_id,
                    ingredient_name: item.ingredient_name,
                    quantity: parseFloat(item.quantity),
                    unit_of_measurement: item.unit_of_measurement,
                    primary_food_group: item.primary_food_group,
                    pantry_ingredient_ids: [item.id],
                    is_confirmed: item.is_confirmed || false,
                    estimated_cost_in_cents: item.estimated_cost_in_cents || 0
                });
            }
        });

        const combined = [];
        
        const allIngredientIds = new Set([
            ...pantryMap.keys(),
            ...groceryMap.keys(),
            ...mealPlanRequiredMap.keys()
        ]);

        allIngredientIds.forEach(ingredientId => {
            const pantryItem = pantryMap.get(ingredientId);
            const groceryItem = groceryMap.get(ingredientId);
            const mealPlanRequiredItem = mealPlanRequiredMap.get(ingredientId);
            
            const pantryQty = pantryItem?.quantity || 0;
            const requiredQty = mealPlanRequiredItem?.quantity || 0;
            const additionalNeeded = isGreaterOrEqual(pantryQty, requiredQty) ? 0 : Math.max(0, requiredQty - pantryQty);
            
            const ingredientName = mealPlanRequiredItem?.ingredient_name || pantryItem?.ingredient_name || groceryItem?.ingredient_name;
            
            if (!ingredientName) {
                return;
            }
            
            let estimatedCostForNeededQuantity = 0;
            if (pantryItem?.estimated_cost_in_cents && pantryQty > 0 && requiredQty > 0) {
                const quantityToUse = Math.min(pantryQty, requiredQty);
                estimatedCostForNeededQuantity = Math.round((pantryItem.estimated_cost_in_cents / pantryQty) * quantityToUse);
            }
            
            combined.push({
                ingredient_id: ingredientId,
                ingredient_name: ingredientName,
                pantry_quantity: pantryQty,
                grocery_quantity: requiredQty,
                additional_needed: additionalNeeded,
                unit_of_measurement: pantryItem?.unit_of_measurement || groceryItem?.unit_of_measurement,
                primary_food_group: pantryItem?.primary_food_group || groceryItem?.primary_food_group || 'Other',
                pantry_ingredient_ids: pantryItem?.pantry_ingredient_ids || [],
                is_needed_for_grocery: requiredQty > 0,
                is_confirmed: pantryItem?.is_confirmed || false,
                estimated_cost_in_cents: estimatedCostForNeededQuantity
            });
        });

        combined.sort((a, b) => {
            if (a.is_needed_for_grocery !== b.is_needed_for_grocery) {
                return b.is_needed_for_grocery ? 1 : -1;
            }
            if (a.primary_food_group !== b.primary_food_group) {
                return a.primary_food_group.localeCompare(b.primary_food_group);
            }
            return a.ingredient_name.localeCompare(b.ingredient_name);
        });
        
        console.log('✅ [PantryAndGroceriesDrawer] Combined data result, total items:', combined.length);
        console.log('📊 [PantryAndGroceriesDrawer] Items needed for grocery:', combined.filter(i => i.is_needed_for_grocery).length);
        
        return combined;
    }, []);

    // Categorize ingredients into two sections
    const categorizeIngredients = useCallback((data) => {
        const neededThisWeek = [];
        const notUsedThisWeek = [];

        data.forEach(item => {
            if (item.grocery_quantity > 0) {
                neededThisWeek.push(item);
            } else {
                notUsedThisWeek.push(item);
            }
        });

        return { neededThisWeek, notUsedThisWeek };
    }, []);

    // Fetch all data when drawer opens
    useEffect(() => {
        if (!isOpen || !pantryId || !pgUser?.id) return;

        const fetchAllData = async () => {
            console.log('🚀 [PantryAndGroceriesDrawer] Starting data fetch...');
            console.log('  - isOpen:', isOpen);
            console.log('  - pantryId:', pantryId);
            console.log('  - pgUser.id:', pgUser?.id);
            console.log('  - mealPlanId prop:', mealPlanId);
            
            setIsLoading(true);
            setError(null);

            try {
                const [groceryData, pantryData] = await Promise.all([
                    fetchCurrentGroceryList(),
                    fetchPantryIngredients()
                ]);

                const mealPlanIdFromGroceryList = groceryData?.grocery_list?.meal_plan_id;
                console.log('📍 [PantryAndGroceriesDrawer] Meal plan ID from grocery list:', mealPlanIdFromGroceryList);
                
                const mealPlanRequiredIngredients = mealPlanIdFromGroceryList 
                    ? await fetchMealPlanRequiredIngredients(mealPlanIdFromGroceryList)
                    : [];

                setCurrentGroceryList(groceryData);
                setPantryIngredients(pantryData);

                const combined = combineData(pantryData, groceryData, mealPlanRequiredIngredients);
                setCombinedData(combined);

                const categorized = categorizeIngredients(combined);
                console.log('📂 [PantryAndGroceriesDrawer] Categorized:', {
                    neededThisWeek: categorized.neededThisWeek.length,
                    notUsedThisWeek: categorized.notUsedThisWeek.length
                });
                
                const sortedNeededThisWeek = [...categorized.neededThisWeek].sort((a, b) => {
                    // Check if item has a valid state (Enough or Not Enough)
                    const aHasState = a.is_confirmed && (isGreaterOrEqual(a.pantry_quantity, a.grocery_quantity) || a.pantry_quantity === 0);
                    const bHasState = b.is_confirmed && (isGreaterOrEqual(b.pantry_quantity, b.grocery_quantity) || b.pantry_quantity === 0);
                    
                    // First sort by state status (items without state first)
                    if (aHasState !== bHasState) {
                        return aHasState ? 1 : -1;
                    }
                    // Second sort by food group
                    const aFoodGroup = a.primary_food_group || 'Other';
                    const bFoodGroup = b.primary_food_group || 'Other';
                    if (aFoodGroup !== bFoodGroup) {
                        return aFoodGroup.localeCompare(bFoodGroup);
                    }
                    // Third sort alphabetically by ingredient name
                    return a.ingredient_name.localeCompare(b.ingredient_name);
                });
                
                setInitialCategories({
                    neededThisWeek: sortedNeededThisWeek,
                    notUsedThisWeek: categorized.notUsedThisWeek
                });
                
                const confirmedIngredients = sortedNeededThisWeek.filter(item => item.is_confirmed);
                const confirmedIngredientIds = confirmedIngredients.map(item => item.ingredient_id);
                setCheckedIngredients(new Set(confirmedIngredientIds));
                
                const initialButtonStates = new Map();
                confirmedIngredients.forEach(item => {
                    if (isGreaterOrEqual(item.pantry_quantity, item.grocery_quantity) && item.grocery_quantity > 0) {
                        initialButtonStates.set(item.ingredient_id, 'enough');
                    } else {
                        initialButtonStates.set(item.ingredient_id, 'none');
                    }
                });
                setButtonStates(initialButtonStates);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load data. Please refresh and try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, [isOpen, pantryId, pgUser?.id, fetchMealPlanRequiredIngredients, fetchCurrentGroceryList, fetchPantryIngredients, combineData, categorizeIngredients]);

    // Get current data with updated quantities from combinedData
    const getCategorizedDataWithUpdates = () => {
        if (!initialCategories) {
            return { neededThisWeek: [], notUsedThisWeek: [] };
        }

        const currentDataMap = new Map();
        combinedData.forEach(item => {
            currentDataMap.set(item.ingredient_id, item);
        });

        const filterAndUpdate = (items) => {
            return items
                .map(item => currentDataMap.get(item.ingredient_id) || item)
                .filter(item => 
                    item?.ingredient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
                );
        };

        return {
            neededThisWeek: filterAndUpdate(initialCategories.neededThisWeek),
            notUsedThisWeek: filterAndUpdate(initialCategories.notUsedThisWeek)
        };
    };

    const { neededThisWeek, notUsedThisWeek } = getCategorizedDataWithUpdates();

    // Calculate progress - count confirmed ingredients that have been verified
    const totalNeeded = neededThisWeek.length;
    const confirmedCount = neededThisWeek.filter(item => {
        return checkedIngredients.has(item.ingredient_id);
    }).length;
    const progressPercentage = totalNeeded > 0 ? Math.round((confirmedCount / totalNeeded) * 100) : 0;

    // Calculate total savings - include only 'enough' state
    const ingredientsWithSavings = neededThisWeek.filter(item => {
        const buttonState = buttonStates.get(item.ingredient_id);
        const hasEnoughStatus = buttonState === 'enough';
        const isConfirmed = checkedIngredients.has(item.ingredient_id);
        return hasEnoughStatus && isConfirmed;
    });
    
    const totalSavingsCents = ingredientsWithSavings.reduce((sum, item) => {
        return sum + item.estimated_cost_in_cents;
    }, 0);
    const totalSavingsDollars = (totalSavingsCents / 100).toFixed(2);
    const ingredientsWithSavingsCount = ingredientsWithSavings.length;

    // Group "Not Used This Week" by food group
    const groupByFoodGroup = (items) => {
        return items.reduce((acc, item) => {
            const group = item.primary_food_group || 'Other';
            if (!acc[group]) acc[group] = [];
            acc[group].push(item);
            return acc;
        }, {});
    };

    const notUsedGrouped = groupByFoodGroup(notUsedThisWeek);

    // Determine button state based on pantry vs required quantity
    const getButtonState = (item) => {
        if (buttonStates.has(item.ingredient_id)) {
            return buttonStates.get(item.ingredient_id);
        }
        
        if (!checkedIngredients.has(item.ingredient_id)) {
            return null;
        }
        
        const hasEnough = isGreaterOrEqual(item.pantry_quantity, item.grocery_quantity);
        const hasRequirement = item.grocery_quantity > 0;
        
        if (hasEnough && hasRequirement) {
            return 'enough';
        } else {
            return 'none';
        }
    };

    // Handle button clicks
    const handleButtonClick = async (item, buttonType) => {
        setButtonStates(prev => {
            const newMap = new Map(prev);
            newMap.set(item.ingredient_id, buttonType);
            return newMap;
        });
        
        setTimeout(async () => {
            if (buttonType === 'enough') {
                const newQuantity = item.grocery_quantity;
                
                setCombinedData(prev => prev.map(i => 
                    i.ingredient_id === item.ingredient_id
                        ? { 
                            ...i, 
                            pantry_quantity: newQuantity,
                            additional_needed: isGreaterOrEqual(newQuantity, i.grocery_quantity) ? 0 : Math.max(0, i.grocery_quantity - newQuantity),
                            is_confirmed: true
                          }
                        : i
                ));
                
                setCheckedIngredients(prev => new Set(prev).add(item.ingredient_id));
                
                try {
                    const result = await confirmHasEnough(
                        item.ingredient_id,
                        pantryId,
                        pgUser.id,
                        newQuantity,
                        item.unit_of_measurement
                    );
                    
                    if (result && result.id && item.pantry_ingredient_ids.length === 0) {
                        setCombinedData(prev => prev.map(i => 
                            i.ingredient_id === item.ingredient_id
                                ? { ...i, pantry_ingredient_ids: [result.id] }
                                : i
                        ));
                    }
                    
                    await handleItemsAddedToPantry();
                    setGroceryDrawerRefreshKey(prev => prev + 1);
                    
                    showToastNotification(item.ingredient_name + ' removed from grocery list');
                } catch (error) {
                    console.error('Failed to confirm quantity:', error);
                    setCombinedData(prev => prev.map(i => 
                        i.ingredient_id === item.ingredient_id
                            ? { 
                                ...i, 
                                pantry_quantity: item.pantry_quantity,
                                additional_needed: item.additional_needed,
                                is_confirmed: item.is_confirmed
                              }
                            : i
                    ));
                    setButtonStates(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(item.ingredient_id);
                        return newMap;
                    });
                    setCheckedIngredients(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(item.ingredient_id);
                        return newSet;
                    });
                    showToastNotification('Failed to confirm quantity', 'error');
                }
            } else if (buttonType === 'none') {
                const newQuantity = 0;
                
                setCombinedData(prev => prev.map(i => 
                    i.ingredient_id === item.ingredient_id
                        ? { 
                            ...i, 
                            pantry_quantity: newQuantity,
                            additional_needed: i.grocery_quantity,
                            is_confirmed: true
                          }
                        : i
                ));
                
                setCheckedIngredients(prev => new Set(prev).add(item.ingredient_id));
                
                try {
                    const result = await confirmHasLessThanEnough(
                        item.ingredient_id,
                        pantryId,
                        pgUser.id,
                        newQuantity,
                        item.unit_of_measurement
                    );
                    
                    if (result && result.id && item.pantry_ingredient_ids.length === 0) {
                        setCombinedData(prev => prev.map(i => 
                            i.ingredient_id === item.ingredient_id
                                ? { ...i, pantry_ingredient_ids: [result.id] }
                                : i
                        ));
                    }
                    
                    await handleItemsAddedToPantry();
                    setGroceryDrawerRefreshKey(prev => prev + 1);
                    
                    // showToastNotification(`${formatQuantity(item.grocery_quantity)} ${item.unit_of_measurement} of ${item.ingredient_name} added to grocery list`);
                } catch (error) {
                    console.error('Failed to confirm quantity:', error);
                    setCombinedData(prev => prev.map(i => 
                        i.ingredient_id === item.ingredient_id
                            ? { 
                                ...i, 
                                pantry_quantity: item.pantry_quantity,
                                additional_needed: item.additional_needed,
                                is_confirmed: item.is_confirmed
                              }
                            : i
                    ));
                    setButtonStates(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(item.ingredient_id);
                        return newMap;
                    });
                    setCheckedIngredients(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(item.ingredient_id);
                        return newSet;
                    });
                    showToastNotification('Failed to confirm quantity', 'error');
                }
            }
        }, 0);
    };

    // Render ingredient item
    const renderIngredientItem = (item, borderColor = 'transparent', showButtons = false) => {
        const currentState = getButtonState(item);
        
        return (
            <div
                key={item.ingredient_id}
                style={{
                    borderBottom: '1px solid #e0e0e0',
                    padding: '16px 8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                }}
            >
                {/* Top row: Icon, Name, and Quantity */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <img
                            src={getIngredientImageUrl(item.ingredient_id)}
                            alt={item.ingredient_name}
                            style={{
                                width: '40px',
                                height: '40px',
                                // borderRadius: '4px',
                                marginRight: '12px',
                                objectFit: 'cover',
                                // border: '1px solid #e0e0e0'
                            }}
                            onError={e => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiNFMEUwRTAiLz48L3N2Zz4=';
                            }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {/* Ingredient name */}
                            <span style={{ fontWeight: '400', fontSize: '1rem', marginBottom: '4px' }}>
                                {item.ingredient_name}
                            </span>
                            {/* "We think you have" text OR feedback text */}
                            {showButtons && (
                                <div 
                                    style={{ 
                                        fontSize: '0.875rem', 
                                        color: '#666',
                                        fontStyle: checkedIngredients.has(item.ingredient_id) && 
                                                   (currentState === 'enough' || currentState === 'none') 
                                                   ? 'normal' : 'italic',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    {checkedIngredients.has(item.ingredient_id) ? (
                                        currentState === 'enough' && item.additional_needed === 0 ? (
                                            <>
                                                <img 
                                                    src="/icons/checkmark-solid.svg" 
                                                    alt="Check" 
                                                    style={{ width: '14px', height: '14px', marginRight: '2px' }} 
                                                />
                                                <span>All Set!</span>
                                            </>
                                        ) : currentState === 'none' && item.additional_needed > 0 ? (
                                            <>
                                                <img 
                                                    src="/icons/shopping-cart-solid_orange.svg" 
                                                    alt="Cart" 
                                                    style={{ width: '14px', height: '14px', marginRight: '6px' }} 
                                                />
                                                <span>We'll add {formatQuantity(item.grocery_quantity)} {item.unit_of_measurement} to your grocery list</span>
                                            </>
                                        ) : (
                                            `We think you have ${formatQuantity(item.pantry_quantity)} ${item.unit_of_measurement}`
                                        )
                                    ) : (
                                        `We think you have ${formatQuantity(item.pantry_quantity)} ${item.unit_of_measurement}`
                                    )}
                                </div>
                            )}
                            {!showButtons && (
                                <span style={{ fontWeight: '500', fontSize: '0.9rem' }}>
                                    {formatQuantity(item.pantry_quantity)} {item.unit_of_measurement}
                                </span>
                            )}
                        </div>
                    </div>
                    
                    {/* Required quantity on the right */}
                    {showButtons && (
                        <span style={{ fontWeight: '700', fontSize: '1rem', whiteSpace: 'nowrap', marginLeft: '16px' }}>
                            {formatQuantity(item.grocery_quantity)} {item.unit_of_measurement}
                        </span>
                    )}
                </div>

                {/* Bottom row: Buttons */}
                {showButtons && (
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', justifyContent: 'center', alignItems: 'stretch' }}>
                        {/* Enough Button (left) */}
                        <button
                            onClick={() => handleButtonClick(item, 'enough')}
                            style={{
                                fontSize: '1.15rem',
                                color: currentState === 'enough' ? '#52cd92' : '#495057',
                                backgroundColor: currentState === 'enough' ? '#f0fdf7' : 'white',
                                border: currentState === 'enough' ? '1.5px solid #52cd92' : '1.5px solid #dddddd',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                padding: '8px 8px',
                                fontWeight: '600',
                                transition: 'all 0.2s ease',
                                boxShadow: currentState === 'enough' ? '0 2px 4px rgba(82, 205, 146, 0.3)' : 'none',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                lineHeight: '1.2'
                            }}
                        >
                            Got it
                        </button>
                        
                        {/* Not Enough Button (right) */}
                        <button
                            onClick={() => handleButtonClick(item, 'none')}
                            style={{
                                fontSize: '1.15rem',
                                color: currentState === 'none' ? '#ffa500' : '#495057',
                                backgroundColor: currentState === 'none' ? '#fff9f0' : 'white',
                                border: currentState === 'none' ? '1.5px solid #ffa500' : '1.5px solid #dddddd',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                padding: '8px 8px',
                                fontWeight: '600',
                                transition: 'all 0.2s ease',
                                boxShadow: currentState === 'none' ? '0 2px 4px rgba(255, 165, 0, 0.3)' : 'none',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                lineHeight: '1.2'
                            }}
                        >
                            Need it
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // Format date range for display
    const formatDateRange = (startDate, endDate) => {
        if (!startDate || !endDate) return '';
        
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        const formatDate = (date) => {
            const month = date.toLocaleString('en-US', { month: 'short' });
            const day = date.getDate();
            return `${month} ${day}`;
        };
        
        return `${formatDate(start)} - ${formatDate(end)}`;
    };

    // Handle items added to pantry
    const handleItemsAddedToPantry = useCallback(async () => {
        const [groceryData, pantryData] = await Promise.all([
            fetchCurrentGroceryList(),
            fetchPantryIngredients()
        ]);

        const mealPlanId = groceryData?.grocery_list?.meal_plan_id;
        
        const mealPlanRequiredIngredients = mealPlanId 
            ? await fetchMealPlanRequiredIngredients(mealPlanId)
            : [];

        setCurrentGroceryList(groceryData);
        setPantryIngredients(pantryData);

        const combined = combineData(pantryData, groceryData, mealPlanRequiredIngredients);
        setCombinedData(combined);
        
        // Don't update initialCategories here - maintain the original sort order from initial load
        // Just update checkedIngredients and buttonStates based on the refreshed data
        const confirmedIngredients = combined.filter(item => item.is_confirmed && item.grocery_quantity > 0);
        const confirmedIngredientIds = confirmedIngredients.map(item => item.ingredient_id);
        setCheckedIngredients(prev => {
            const merged = new Set(prev);
            confirmedIngredientIds.forEach(id => merged.add(id));
            return merged;
        });
        
        setButtonStates(prevButtonStates => {
            const refreshedButtonStates = new Map(prevButtonStates);
            
            confirmedIngredients.forEach(item => {
                const hasEnough = isGreaterOrEqual(item.pantry_quantity, item.grocery_quantity);
                const hasRequirement = item.grocery_quantity > 0;
                
                if (hasEnough && hasRequirement) {
                    refreshedButtonStates.set(item.ingredient_id, 'enough');
                } else {
                    refreshedButtonStates.set(item.ingredient_id, 'none');
                }
            });
            
            return refreshedButtonStates;
        });
    }, [fetchMealPlanRequiredIngredients, fetchCurrentGroceryList, fetchPantryIngredients, combineData]);

    return (
        <ErrorBoundary fallbackMessage="Unable to load pantry and groceries. Please try again later.">
            <Offcanvas 
                show={isOpen} 
                onHide={onClose}
                placement={isMobile ? "bottom" : "end"}
                style={{ 
                    width: isMobile ? '100%' : '95%', 
                    maxWidth: isMobile ? '100%' : '500px',
                    height: isMobile ? 'auto' : 'auto',
                    backgroundColor: 'white', 
                    overflow: 'hidden', 
                    display: 'flex', 
                    flexDirection: 'column' 
                }}
            >
                {/* Custom close button in top left */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        // top: '16px',
                        // left: '16px',
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        padding: '0',
                        color: '#666',
                        zIndex: 1050,
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    aria-label="Close"
                >
                    ×
                </button>
                
                <Offcanvas.Header style={{ 
                    borderBottom: 'none', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px', 
                    paddingBottom: '16px', 
                    paddingTop: '24px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.1) 100%)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    // marginBottom: '-48px',
                    flexShrink: 0
                }}>
                    <h2 
                        style={{ 
                            fontWeight: 'bold', 
                            fontSize: '1.25rem', 
                            width: '100%', 
                            textAlign: 'center',
                            margin: 0
                        }}
                    >
                        Do you already have any of these ingredients?
                    </h2>
                    
                    {currentGroceryList && (
                        <>
                            {/* Progress Bar */}
                            {totalNeeded > 0 && (
                                <div style={{ width: '100%', marginBottom: '8px' }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '4px',
                                        fontSize: '1.25rem',
                                        color: '#666'
                                    }}>
                                        <span>
                                            {confirmedCount} of {totalNeeded} ingredients verified
                                            {confirmedCount == totalNeeded && (
                                                <span style={{ fontWeight: '600', marginLeft: '0.25em' }}> 🎉</span>
                                            )}
                                        </span>
                                        {/* <span style={{ fontWeight: '600' }}>{progressPercentage}%</span> */}
                                    </div>
                                    <div style={{
                                        width: '100%',
                                        height: '6px',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '3px',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            width: `${progressPercentage}%`,
                                            height: '100%',
                                            backgroundColor: '#52cd92',
                                            borderRadius: '3px',
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                </div>
                            )}
                            
                            {/* Savings Display */}
                            {totalSavingsCents > 0 && (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '8px 16px',
                                    backgroundColor: '#f0fdf7',
                                    border: '1.5px solid #52cd92',
                                    borderRadius: '6px',
                                    boxShadow: '0 1px 3px rgba(82, 205, 146, 0.1)'
                                }}>
                                    <div style={{
                                        fontSize: '0.8rem',
                                        color: '#666',
                                        marginBottom: '2px'
                                    }}>
                                        You're saving by using <b>{ingredientsWithSavingsCount}</b> {ingredientsWithSavingsCount === 1 ? 'ingredient' : 'ingredients'} you already have
                                    </div>
                                    <div style={{
                                        fontSize: '1.25rem',
                                        fontWeight: '700',
                                        color: '#52cd92'
                                    }}>
                                        ${totalSavingsDollars}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Offcanvas.Header>
                <Offcanvas.Body style={{ 
                    padding: '1rem', 
                    paddingBottom: '0',
                    overflowY: 'auto', 
                    backgroundColor: 'transparent',
                    marginTop: '-48px',
                    paddingTop: '48px',
                    flex: 1
                }}>
                    {isAuthLoading ? (
                        <div className="text-center my-4">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : !pgUser ? (
                        <div className="alert alert-warning">
                            <p className="h5 mb-3">Unable to connect to the server. Please try again later.</p>
                        </div>
                    ) : isLoading ? (
                        <div className="text-center my-4">
                            <div className="spinner-border" role="status" style={{ color: '#52cd92' }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    ) : (
                        <>
                            {(neededThisWeek.length === 0 && notUsedThisWeek.length === 0) ? (
                                <div className="text-center py-5">
                                    <p className="text-muted">No ingredients found</p>
                                </div>
                            ) : (
                                <>
                                    {/* Section 1: Check Your Pantry */}
                                    {neededThisWeek.length > 0 && (
                                        <div className="mb-5">
                                            {neededThisWeek.map((item, index) => {
                                                const currentFoodGroup = item.primary_food_group || 'Other';
                                                const previousFoodGroup = index > 0 ? (neededThisWeek[index - 1].primary_food_group || 'Other') : null;
                                                const showGroupHeader = currentFoodGroup !== previousFoodGroup;
                                                
                                                return (
                                                    <React.Fragment key={item.ingredient_id}>
                                                        {/* Food Group Section Headers */}
                                                        {/* {showGroupHeader && (
                                                            <div style={{
                                                                padding: '12px 8px 8px 8px',
                                                                fontWeight: '700',
                                                                fontSize: '0.9rem',
                                                                color: '#999',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.5px',
                                                                marginTop: index > 0 ? '16px' : '0'
                                                            }}>
                                                                {currentFoodGroup}
                                                            </div>
                                                        )} */}
                                                        {renderIngredientItem(item, '#52cd92', true)}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Section 2: Not Used This Week */}
                                    {/* {notUsedThisWeek.length > 0 && (
                                        <div className="mb-5">
                                            <h3 style={{ 
                                                fontWeight: '700', 
                                                marginTop: '1.5rem', 
                                                marginBottom: '1rem',
                                                textAlign: 'center',
                                                color: '#999',
                                                fontSize: '1.5rem'
                                            }}>
                                                Other Items in Pantry
                                            </h3>
                                            
                                            {Object.keys(notUsedGrouped).sort().map(foodGroup => (
                                                <div key={foodGroup} className="mb-3">
                                                    <h4 style={{ 
                                                        fontWeight: '700', 
                                                        marginTop: '1rem', 
                                                        marginBottom: '0.5rem',
                                                        textAlign: 'left',
                                                        paddingLeft: '10px',
                                                        fontSize: '1.1rem',
                                                        color: '#999'
                                                    }}>
                                                        {foodGroup}
                                                    </h4>
                                                    {notUsedGrouped[foodGroup].map(item => renderIngredientItem(item, 'transparent', false))}
                                                </div>
                                            ))}
                                        </div>
                                    )} */}
                                </>
                            )}

                            {/* Grocery List Button */}
                            {/* {currentGroceryList && (
                                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                                    <Button
                                        onClick={() => setIsGroceryDrawerOpen(true)}
                                        style={{
                                            backgroundColor: '#52cd92',
                                            border: 'none',
                                            color: 'white',
                                            padding: '0.75rem 1.5rem',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            width: '100%',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(82, 205, 146, 0.3)'
                                        }}
                                    >
                                        View Grocery List
                                    </Button>
                                </div>
                            )} */}
                            
                            {/* Spacer for sticky footer */}
                            <div style={{ height: '80px' }} />
                        </>
                    )}
                </Offcanvas.Body>

                {/* Sticky Footer with I'm Done Button */}
                {currentGroceryList && (
                    <div style={{
                        position: 'sticky',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '1rem',
                        backgroundColor: 'white',
                        borderTop: '1px solid #e0e0e0',
                        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        display: 'flex',
                        gap: '0.75rem'
                    }}>
                        <button
                            className="btn btn-secondary"
                            onClick={onClose}
                            style={{
                                padding: '0.75rem 1rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                borderRadius: '8px',
                                flex: 1
                            }}
                        >
                            Close
                        </button>
                        <Button
                            onClick={() => {
                                const week = determineWeekFromDates(
                                    currentGroceryList?.grocery_list?.start_date,
                                    currentGroceryList?.grocery_list?.end_date
                                );
                                onClose();
                                navigate(`/grocery-list?week=${week}`);
                            }}
                            style={{
                                backgroundColor: '#52cd92',
                                border: 'none',
                                color: 'white',
                                padding: '0.75rem 1rem',
                                fontSize: '1rem',
                                fontWeight: '600',
                                flex: 1,
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(82, 205, 146, 0.3)'
                            }}
                        >
                            View Grocery List
                        </Button>
                    </div>
                )}

                {/* Toast Notification */}
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    style={{
                        position: 'fixed',
                        top: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        backgroundColor: toastType === 'success' ? '#52cd92' : '#dc3545',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        minWidth: '300px'
                    }}
                >
                    <Toast.Header
                        style={{
                            backgroundColor: toastType === 'success' ? '#52cd92' : '#dc3545',
                            borderBottom: '1px solid rgba(255,255,255,0.2)',
                            justifyContent: 'center'
                        }}
                    >
                        <strong style={{ color: 'white' }}>{toastMessage}</strong>
                    </Toast.Header>
                </Toast>
            </Offcanvas>

            {/* Grocery Drawer */}
            <GroceryDrawer
                key={groceryDrawerRefreshKey}
                isOpen={isGroceryDrawerOpen}
                onClose={() => setIsGroceryDrawerOpen(false)}
                groceryListId={currentGroceryList?.grocery_list?.id}
                onItemsAddedToPantry={handleItemsAddedToPantry}
            />

            {/* Recipes For Ingredient Drawer */}
            <RecipesForIngredientDrawer
                isOpen={isRecipeDrawerOpen}
                onClose={() => setIsRecipeDrawerOpen(false)}
                ingredient={selectedIngredientForRecipes}
            />
        </ErrorBoundary>
    );
};

export default PantryAndGroceriesDrawerWithAddAdditionalQuantity;

